import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import toast from 'react-hot-toast'
import type { Upload } from '../types'

export default function UploadPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [file, setFile] = useState<File | null>(null)
  const [datasetName, setDatasetName] = useState('')
  const [progress, setProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing' | 'complete' | 'error'>('idle')
  
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file')
        return
      }
      setFile(file)
      setDatasetName(file.name.replace('.csv', ''))
    }
  }, [])
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  })
  
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected')
      
      setUploadStatus('uploading')
      setProgress(0)
      
      // Upload file directly to API using FormData
      const formData = new FormData()
      formData.append('file', file)
      formData.append('dataset_name', datasetName)
      
      const uploadRes = await api.post<Upload>('/uploads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setProgress(Math.round((progressEvent.loaded / progressEvent.total) * 90))
          }
        },
      })
      
      setProgress(90)
      setUploadStatus('processing')
      
      // Poll for processing completion
      let upload = uploadRes.data
      while (upload.status === 'uploaded' || upload.status === 'processing') {
        await new Promise(r => setTimeout(r, 1000))
        const statusRes = await api.get<Upload>(`/uploads/${upload.id}`)
        upload = statusRes.data
        setProgress(90 + (upload.progress_percent || 0) / 10)
      }
      
      if (upload.status === 'failed') {
        throw new Error(upload.error_message || 'Processing failed')
      }
      
      setProgress(100)
      setUploadStatus('complete')
      
      return upload
    },
    onSuccess: (upload) => {
      toast.success('Upload complete!')
      queryClient.invalidateQueries({ queryKey: ['datasets'] })
      if (upload.dataset_id) {
        navigate(`/datasets/${upload.dataset_id}`)
      } else {
        navigate('/datasets')
      }
    },
    onError: (error) => {
      setUploadStatus('error')
      toast.error(error instanceof Error ? error.message : 'Upload failed')
    },
  })
  
  const handleUpload = () => {
    if (!file) {
      toast.error('Please select a file')
      return
    }
    uploadMutation.mutate()
  }
  
  const clearFile = () => {
    setFile(null)
    setDatasetName('')
    setProgress(0)
    setUploadStatus('idle')
  }
  
  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Upload CSV</h1>
        <p className="mt-1 text-sm text-gray-600">
          Upload any CSV file. Column types will be automatically detected.
        </p>
      </div>
      
      <div className="card p-6">
        {!file ? (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              {isDragActive
                ? 'Drop the file here'
                : 'Drag and drop a CSV file, or click to select'}
            </p>
            <p className="mt-1 text-xs text-gray-500">Max file size: 500MB</p>
          </div>
        ) : (
          <div>
            {/* File info */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
              <div className="flex items-center">
                <DocumentIcon className="h-8 w-8 text-gray-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              {uploadStatus === 'idle' && (
                <button
                  onClick={clearFile}
                  className="p-1 text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            
            {/* Dataset name */}
            <div className="mb-4">
              <label htmlFor="datasetName" className="label">
                Dataset Name
              </label>
              <input
                id="datasetName"
                type="text"
                value={datasetName}
                onChange={(e) => setDatasetName(e.target.value)}
                disabled={uploadStatus !== 'idle'}
                className="input"
              />
            </div>
            
            {/* Progress */}
            {uploadStatus !== 'idle' && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>
                    {uploadStatus === 'uploading' && 'Uploading...'}
                    {uploadStatus === 'processing' && 'Processing...'}
                    {uploadStatus === 'complete' && 'Complete!'}
                    {uploadStatus === 'error' && 'Error'}
                  </span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      uploadStatus === 'error' ? 'bg-red-600' : 'bg-primary-600'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Actions */}
            {uploadStatus === 'idle' && (
              <div className="flex gap-3">
                <button
                  onClick={handleUpload}
                  className="btn-primary flex-1"
                >
                  Upload
                </button>
                <button
                  onClick={clearFile}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            )}
            
            {uploadStatus === 'error' && (
              <button
                onClick={clearFile}
                className="btn-secondary w-full"
              >
                Try Again
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
